<script setup>
import { ref } from 'vue';
import { useBreakpoint } from '@/composables/useBreakpoint';
import { useToast } from 'primevue/usetoast';
import CertidaoService from '@/services/CertidaoService';
import Button from 'primevue/button';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import VisualizadorDocumentoUnico from '@/vue/components/VisualizadorDocumentoUnico.vue';
import Header from '@/vue/components/Header.vue';
import Loading from '@/vue/components/Loading.vue';
import FormConsulta from './Components/FormConsulta.vue';
import FormValidacao from './Components/FormValidacao.vue';

const { isMobile } = useBreakpoint();
const toast = useToast();

// LOADING
const loading = ref(false);
const emitindo = ref(false);

// DADOS
const certidoesDisponiveis = ref([]);

// VISUALIZADOR PDF
const pageBodyContainerId = ref('page-body-container');
const visualizadorRef = ref(null);
const formConsultaRef = ref(null);

// CONFIGURAÇÕES
const menuCertidoesVisivel = ref(false);

// HANDLERS FILHOS
const handleLoading = (value) => loading.value = value;

const handleCertidoes = (certidoes) => certidoesDisponiveis.value = certidoes;

// HELPERS
const getTipoDescricao = (tipo) => {
    const tipos = {
        P: 'Positiva',
        N: 'Negativa',
        R: 'Regular'
    };

    return tipos[tipo] ?? tipo;
}

const onlyDigits = (value) => {
    return (value || '').replace(/\D/g, '');
}

const handleApiError = (e, fallbackMsg) => {
    const validationErrors = e.response?.data?.errors;
    const errorMessage = e.response?.data?.message;

    if (validationErrors) {
        const messages = Object.values(validationErrors).flat().join('<br/>');

        toast.add({
            severity: 'info',
            summary: 'Atenção',
            detail: messages,
            life: 5000
        });

        return;
    }

    toast.add({
        severity: 'error',
        summary: 'Algo inesperado aconteceu...',
        detail: errorMessage ?? fallbackMsg,
        life: 5000
    });
}

// EMITIR CERTIDÃO
const emitirCertidao = async (certidao) => {
    if (!certidao?.tipo) return;

    emitindo.value = true;

    const valores = formConsultaRef.value?.valoresConsulta ?? {};

    const payload = {
        emitir_por: valores.emitir_por,
        tipo: certidao.tipo,
        identificador: certidao.identificador,
        origem: 'processo_eletronico'
    }

    if (valores.emitir_por !== 'C') payload.identificador = onlyDigits(valores.identificador);

    const cpfCnpj = onlyDigits(valores.cpf_cnpj);

    if (cpfCnpj) payload.cpf_cnpj = cpfCnpj;

    try {
        const resp = await CertidaoService.emitir(payload);

        const arquivo = resp.data?.data?.arquivo ?? resp.data?.arquivo ?? null;

        if (!arquivo) {
            toast.add({
                severity: 'error',
                summary: 'Algo inesperado aconteceu...',
                detail: 'A emissão não retornou o arquivo da certidão.',
                life: 3000
            });

            return;
        }

        const pdfBase64 = arquivo.replace(/^data:application\/pdf;base64,/, '').trim();
        const fileName = `certidao-${certidao.tipo.toLowerCase()}-${onlyDigits(valores.identificador)}.pdf`;

        if (visualizadorRef.value) {
            visualizadorRef.value.openDialogWithBase64(
                `data:application/pdf;base64,${pdfBase64}`,
                fileName
            );
        }
    } catch (e) {
        handleApiError(e, 'Não foi possível emitir a certidão fiscal.');
    } finally {
        emitindo.value = false;
    }
}
</script>

<template>
    <VisualizadorDocumentoUnico ref="visualizadorRef" :append-to="pageBodyContainerId"/>

    <main class="main-content">
        <Header
            :titulo="
                isMobile ? 'Emissão de certidões por matrícula ou inscrição e validação' : 'Certidões Fiscais'
            "
            titulo-mobile-header="CertidõesFiscais"
            :subtitulo="
                isMobile ?
                'Informe o tipo de identificação, preencha o número correspondente e, se necessário, ' +
                'adicione CPF ou CNPJ para consultar e emitir a certidão fiscal disponível.' :
                'Emissão de certidões por matrícula ou inscrição e validação'
            "
            sub-titulo-mobile-header=""
        />

        <Loading :is-loading="loading" message="" class="justify"/>

        <div :id="pageBodyContainerId" class="position-relative h-100" style="min-height: 0;">
            <div v-show="!loading" class="certidoes-page">
                <div class="workspace">
                    <!-- VALIDAÇÃO -->
                    <FormValidacao
                        @loading="handleLoading"
                    />

                    <!-- CONSULTA -->
                    <FormConsulta
                        ref="formConsultaRef"
                        :menu-certidoes-visivel="menuCertidoesVisivel"
                        :emitindo="emitindo"
                        @loading="handleLoading"
                        @certidoes="handleCertidoes"
                    />

                    <!-- RESULTADO CERTIDÕES DISPONÍVEIS -->
                    <div v-if="certidoesDisponiveis.length > 0" class="resultado">
                        <div class="resultado-header">
                            <div>
                                <span class="resultado-label">Resultado da listagem</span>

                                <h2>Tipos disponíveis para emissão</h2>
                            </div>
                        </div>

                        <div class="certidoes-grid">
                            <Card
                                v-for="certidao in certidoesDisponiveis"
                                :key="certidao.tipo"
                                class="certidao-card"
                            >
                                <template #title>
                                    <div class="certidao-header">
                                        <Tag :value="certidao.tipo" severity="primary"/>

                                        <h3>{{ certidao.titulo }}</h3>
                                    </div>
                                </template>

                                <template #content>
                                    <p class="certidao-descricao">{{ getTipoDescricao(certidao.tipo) }}</p>

                                    <Button
                                        :label="emitindo ? 'Emitindo...' : 'Emitir certidão'"
                                        icon="pi pi-download"
                                        severity="accent"
                                        type="button"
                                        @click="emitirCertidao(certidao)"
                                        :loading="emitindo"
                                        :disabled="emitindo"
                                        class="w-full"
                                    />
                                </template>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
.main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: var(--color-background);
}

.justify {
    flex: 1;
    display: flex;
    justify-content: center;
    margin-top: 200px;
}

.certidoes-page {
    height: 100%;
    max-width: 1200px;
    padding: 20px;
    overflow: overlay;
}

.workspace {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.consulta-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: none;
}

.card-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-primary);
    margin: 0;
}

.field label {
    font-weight: 500;
    font-size: 0.875rem;
    color: #334155;
}

/* RESULTADO VALIDACAO */
.resultado-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
}

.resultado-header h2 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--color-primary);
    margin: 4px 0 0 0;
}

.resultado-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.dados-grid div {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.dados-grid dt {
    font-size: 0.75rem;
    font-weight: 500;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.dados-grid dd {
    font-size: 0.95rem;
    font-weight: 500;
    color: #0f172a;
    margin: 0;
}

/* RESULTADO CERTIDOES */
.resultado {
    margin-top: 8px;
}

.resultado .resultado-header h2 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--color-primary);
    margin: 4px 0 0 0;
}

.certidoes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
    margin-top: 16px;
}

.certidao-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: none;
}

.certidao-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
}

.certidao-header h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
}

.certidao-descricao {
    font-size: 0.875rem;
    color: #64748b;
    margin: 8px 0 16px 0;
}

@media (max-width: 767px) {
    .certidoes-page {
        padding: 12px;
    }

    .form-grid {
        grid-template-columns: 1fr;
    }

    .actions {
        flex-direction: column;
    }

    .dados-grid {
        grid-template-columns: 1fr;
    }

    .resultado-header {
        flex-direction: column;
        gap: 12px;
    }

    .certidoes-grid {
        grid-template-columns: 1fr;
    }
}
</style>
